import React from 'react';
import { Badge } from '../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';

interface NumberResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const NumberResponse: React.FC<NumberResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, answered_count, options } = question;
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Calculate statistics from the options data
    // For number questions, options contain the actual numeric values and their counts
    const numericValues: number[] = [];
    options.forEach(option => {
        const value = parseFloat(option.label);
        if (!isNaN(value)) {
            // Add the value 'count' times to the array
            for (let i = 0; i < option.count; i++) {
                numericValues.push(value);
            }
        }
    });
    
    // Sort values for median calculation
    const sortedValues = [...numericValues].sort((a, b) => a - b);
    
    // Calculate statistics
    const average = numericValues.length > 0 
        ? (numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length).toFixed(1)
        : '0.0';
    
    const median = sortedValues.length > 0
        ? (() => {
            const mid = Math.floor(sortedValues.length / 2);
            return sortedValues.length % 2 === 0
                ? ((sortedValues[mid - 1] + sortedValues[mid]) / 2).toFixed(1)
                : sortedValues[mid].toFixed(1);
        })()
        : '0.0';
    
    const minimum = sortedValues.length > 0 ? sortedValues[0].toString() : '0';
    const maximum = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].toString() : '0';
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.93262 7.71973H17.266" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.93262 12.7197H17.266" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.93327 2.71973L7.2666 17.7197" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9333 2.71973L12.2666 17.7197" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                        >
                            Number
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Statistics Section */}
            {numericValues.length > 0 ? (
                <div className="grid grid-cols-4 gap-4">
                    {/* Average */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {average}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            Average
                        </div>
                    </div>
                    
                    {/* Median */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {median}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            Median
                        </div>
                    </div>
                    
                    {/* Minimum */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {minimum}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            Minimum
                        </div>
                    </div>
                    
                    {/* Maximum */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {maximum}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            Maximum
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            )}
        </div>
    );
};

export default NumberResponse;
